import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { User, Eye, EyeOff, AlertCircle, Calendar, Tag, BarChart3, DollarSign, Shield, Rocket } from 'lucide-react';
import { post } from '../../api/client';
import { useSiteConfig, landingStatCells } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';
import ScanLoginQrCode from '../../components/auth/ScanLoginQrCode';

// 登录模式类型
type LoginMode = 'account' | 'qrcode';


// 主页面组件
export default function AuthPage() {
  // 状态管理
  const [loginMode, setLoginMode] = useState<LoginMode>('account');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // 扫码组件全页只能挂一份：窄屏与宽屏两套布局都常驻 React 树（Tailwind 的 hidden
  // 只是 CSS 隐藏，DOM 照样挂载），于是每次进登录页都会创建 2 个扫码场景、跑 2 个轮询，
  // 各自的 5 分钟自动刷新还不同相——手机扫到的 key 与浏览器正在轮询的 key 一旦不是同一个，
  // 表现就是手机上「登录确认成功」、PC 端永远不动。
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  
  // 登录相关状态
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(''); // 未验证邮箱的用户ID
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  
  // 钩子
  const { signInWithIdentifier, user, loading } = useAuthStore();
  const navigate = useNavigate();
  const { siteName, logoUrl, landingStats } = useSiteConfig();
  const statCells = landingStatCells(landingStats);
  
  // 防止已登录用户访问登录页
  useEffect(() => {
    if (!loading && user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  // 监听滚动事件，控制导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 断点跟随：与上面两套布局用的 lg:（1024px）保持一致，跨断点时把扫码组件搬到新分支
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 登录处理函数
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      setLoginError('请输入账号');
      return;
    }
    
    if (!password) {
      setLoginError('请输入密码');
      return;
    }
    
    setSubmitting(true);
    setLoginError('');
    
    try {
      // 自动检测登录类型
      let loginType: 'email' | 'username' | 'phone';
      if (/^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+$/.test(identifier)) {
        loginType = 'email';
      } else if (/^1[3-9]\d{9}$/.test(identifier)) {
        loginType = 'phone';
      } else {
        loginType = 'username';
      }
      
      const { error } = await signInWithIdentifier(
        identifier,
        password,
        loginType
      );
      
      if (error) {
        const apiErr = error as { code?: string; data?: { email?: string } };
        if (apiErr.code === 'EMAIL_NOT_VERIFIED') {
          setLoginError('');
          setEmailNotVerified(apiErr.data?.email || identifier);
          setResendMsg('');
        } else {
          setLoginError(error.message || '登录失败，请重试');
          setEmailNotVerified('');
        }
        setSubmitting(false);
        return;
      }
      
      // 登录成功，跳转到首页
      navigate('/', { replace: true });
    } catch (ex) {
      console.error('登录异常:', ex);
      setLoginError('网络异常，请重试');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* 顶部导航 */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 hidden md:block ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="w-10 h-10"
            />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              产品文档
            </a>
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/download" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              下载 App
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a 
              href="/login" 
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'border border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* 主屏区 */}
      <section className="relative min-h-screen overflow-hidden flex items-center justify-center">
        {/* 第一层：深蓝绿渐变底色 */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f2027] via-[#203a43] to-[#2c5364]"></div>
        
        {/* 第二层：背景图（30%透明度） */}
        <div className="absolute inset-0 opacity-30">
          <img
            src="/brand/login-bg.jpg"
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* 第三层：渐变遮罩 */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-teal-950/40 to-slate-900/80"></div>
        
        {/* 第四层：装饰光晕 */}
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-emerald-500/8 rounded-full blur-3xl"></div>

        {/* 移动端布局：登录框在上，介绍在下 */}
        <div className="lg:hidden relative z-10">
          {/* 登录框区 */}
          <div className="min-h-screen flex items-center justify-center px-8 py-10">
            <div className="w-full max-w-[420px] sm:min-w-[360px]">
              <div className="bg-white rounded-2xl shadow-2xl px-8 pt-8 pb-8">
                <div className="text-center mb-6">
                  <a href="/login" className="inline-block">
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      className="w-10 h-10 mx-auto mb-3"
                    />
                    <h2 className="text-lg font-bold text-slate-800">{siteName}</h2>
                  </a>
                  <p className="text-slate-500 mt-1 text-sm">欢迎登录</p>
                </div>

                {/* 登录模式切换 */}
                <div className="flex bg-slate-100 rounded-full p-1 mb-6">
                  <button
                    onClick={() => setLoginMode('account')}
                    className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${loginMode === 'account' ? 'bg-white text-slate-800 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                  >
                    账号登录
                  </button>
                  <button
                    onClick={() => setLoginMode('qrcode')}
                    className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${loginMode === 'qrcode' ? 'bg-white text-slate-800 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                  >
                    扫码登录
                  </button>
                </div>

                {/* 账号登录 */}
                {loginMode === 'account' && (
                  <form onSubmit={handleLogin} className="space-y-4">
                    {/* 三合一输入框 */}
                    <div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                          type="text"
                          value={identifier}
                          onChange={(e) => { setIdentifier(e.target.value); setLoginError(''); }}
                          placeholder="手机号 / 邮箱 / 用户名"
                          className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                        />
                      </div>
                    </div>

                    {/* 密码输入 */}
                    <div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Eye className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                          placeholder="请输入密码"
                          className="w-full pl-11 pr-11 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5 text-slate-400" /> : <Eye className="h-5 w-5 text-slate-400" />}
                        </button>
                      </div>
                    </div>

                    {/* 错误提示 */}
                    {loginError && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <span className="text-sm text-red-600">{loginError}</span>
                      </div>
                    )}

                    {/* 邮箱未验证提示 */}
                    {emailNotVerified && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-700">
                            <p>请先验证邮箱后再登录</p>
                            <button
                              type="button"
                              disabled={resending}
                              onClick={async () => {
                                setResending(true);
                                setResendMsg('');
                                try {
                                  await post('/auth/resend-activation', { email: emailNotVerified });
                                  setResendMsg('激活邮件已重新发送，请查收');
                                } catch (e) {
                                  setResendMsg(e instanceof Error ? e.message : '发送失败，请稍后重试');
                                } finally {
                                  setResending(false);
                                }
                              }}
                              className="mt-1.5 text-amber-600 hover:text-amber-700 underline underline-offset-2 text-xs"
                            >
                              {resending ? '发送中...' : '重新发送激活邮件'}
                            </button>
                            {resendMsg && <p className="mt-1 text-xs text-amber-600">{resendMsg}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 记住我和忘记密码 */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={() => setRememberMe(!rememberMe)}
                          className="w-4 h-4 text-teal-500 rounded border-slate-300 focus:ring-teal-500"
                        />
                        <span className="text-xs text-slate-600">记住我</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-xs text-teal-600 hover:text-teal-700 transition-colors"
                      >
                        忘记密码？
                      </button>
                    </div>

                    {/* 登录按钮 */}
                    <button 
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          登录中...
                        </>
                      ) : '登录'}
                    </button>

                    {/* 注册链接 */}
                    <p className="text-center text-xs text-slate-500">
                      还没有账号？
                      <button
                        type="button"
                        onClick={() => navigate('/register')}
                        className="text-teal-600 hover:text-teal-700 font-semibold ml-1"
                      >
                        立即注册
                      </button>
                    </p>
                  </form>
                )}

                {/* 扫码登录 */}
                {loginMode === 'qrcode' && !isDesktop && (
                  <ScanLoginQrCode />
                )}

                {/* 忘记密码已迁至独立路由 /forgot-password */}
              </div>
            </div>
          </div>

          {/* 介绍内容区 */}
          <div className="px-8 pb-16 pt-4 relative z-10">
            {/* 标签徽章 */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-teal-400/40 mb-8">
              <div className="w-1.5 h-1.5 bg-teal-400 animate-pulse"></div>
              <span className="text-xs font-medium tracking-widest text-teal-300">企业级 SaaS 管理平台</span>
            </div>

            {/* 主标题：落地页唯一的 h1，带「订单预约」「管理平台」两个检索词。
                Tailwind preflight 把 h1 的字号字重都重置成 inherit，换标签不动视觉。 */}
            <h1 className="font-bold leading-tight mb-6">
              <div className="text-3xl text-white">订单预约</div>
              <div className="text-3xl text-teal-300 mt-1">管理服务平台</div>
            </h1>

            {/* 副标题描述 */}
            <p className="max-w-xl text-sm leading-relaxed mb-10 text-slate-300">
              一站式订单预约管理平台，帮助您轻松追踪客户、管理预约、分析数据，
              <strong className="text-teal-200 font-medium"> 让每一笔业务都有据可查。</strong>
            </p>

            {/* 功能标签组 */}
            <div className="flex flex-wrap gap-2 mb-12">
              {[
                { icon: <Calendar className="h-4 w-4" />, text: '智能预约日历' },
                { icon: <Tag className="h-4 w-4" />, text: '多维客户标签' },
                { icon: <BarChart3 className="h-4 w-4" />, text: '实时数据分析' },
                { icon: <Shield className="h-4 w-4" />, text: '安全权限隔离' }
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-white/8 border border-white/15 rounded-full backdrop-blur-sm">
                  <div className="w-4 h-4 flex items-center justify-center text-teal-300">
                    {feature.icon}
                  </div>
                  <span className="text-xs text-white/85">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* 统计数据行：四格读数由后台「站点信息」按真实统计维护、经 /public/config 下发。
                原先这里写死的是口号式数量级，属广告引证内容（广告法第十一条第二款要求真实、准确
                并标明出处）；未配置的格整块不渲染，四格全空则整行不出现，不兜底任何编造数字。 */}
            {statCells.length > 0 && (
              <div className="grid grid-cols-2 gap-5">
                {statCells.map((cell) => (
                  <div key={cell.label} className="text-center">
                    <div className="text-xl font-bold text-teal-300">{cell.value}</div>
                    <div className="text-xs text-slate-400 mt-1">{cell.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 桌面端布局：左右并排 */}
        <div className="hidden lg:block relative z-10 w-full">
          <div className="max-w-screen-xl mx-auto px-10">
            <div className="flex items-center gap-16">
              {/* 左侧介绍内容 */}
              <div className="flex-1">
                {/* 标签徽章 */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-teal-400/40 mb-8">
                  <div className="w-1.5 h-1.5 bg-teal-400 animate-pulse"></div>
                  <span className="text-xs font-medium tracking-widest text-teal-300">企业级 SaaS 管理平台</span>
                </div>

                {/* 主标题：桌面分支与上面移动端分支是同一份文案的两套布局，DOM 里两处 h1 文字完全一致，
                    搜索引擎取到的是同一个标题 */}
                <h1 className="font-bold leading-tight mb-6">
                  <div className="text-5xl xl:text-[56px] text-white">订单预约</div>
                  <div className="text-5xl xl:text-[56px] text-teal-300 mt-1">管理服务平台</div>
                </h1>

                {/* 副标题描述 */}
                <p className="max-w-xl text-lg leading-relaxed mb-10 text-slate-300">
                  一站式订单预约管理平台，帮助您轻松追踪客户、管理预约、分析数据，
                  <strong className="text-teal-200 font-medium"> 让每一笔业务都有据可查。</strong>
                </p>

                {/* 功能标签组 */}
                <div className="flex flex-wrap gap-3 mb-12">
                  {[
                    { icon: <Calendar className="h-4 w-4" />, text: '智能预约日历' },
                    { icon: <Tag className="h-4 w-4" />, text: '多维客户标签' },
                    { icon: <BarChart3 className="h-4 w-4" />, text: '实时数据分析' },
                    { icon: <Shield className="h-4 w-4" />, text: '安全权限隔离' }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 px-4 py-2 bg-white/8 border border-white/15 rounded-full backdrop-blur-sm">
                      <div className="w-4 h-4 flex items-center justify-center text-teal-300">
                        {feature.icon}
                      </div>
                      <span className="text-sm text-white/85">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* 统计数据行：与上方移动端分支同一份 statCells，同样只渲染后台配了的格 */}
                {statCells.length > 0 && (
                  <div className="grid grid-cols-4 gap-6">
                    {statCells.map((cell) => (
                      <div key={cell.label} className="text-left">
                        <div className="text-2xl font-bold text-teal-300">{cell.value}</div>
                        <div className="text-xs text-slate-400 mt-1">{cell.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 右侧登录框 */}
              <div className="w-auto max-w-[420px] sm:min-w-[360px]">
                <div className="bg-white rounded-2xl shadow-2xl px-8 pt-8 pb-8">
                  <div className="text-center mb-6">
                    <a href="/login" className="inline-block">
                      <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="w-10 h-10 mx-auto mb-3"
                      />
                      <h2 className="text-lg font-bold text-slate-800">{siteName}</h2>
                    </a>
                    <p className="text-slate-500 mt-1 text-sm">欢迎登录</p>
                  </div>

                  {/* 登录模式切换 */}
                  <div className="flex bg-slate-100 rounded-full p-1 mb-6">
                    <button
                      onClick={() => setLoginMode('account')}
                      className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${loginMode === 'account' ? 'bg-white text-slate-800 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      账号登录
                    </button>
                    <button
                      onClick={() => setLoginMode('qrcode')}
                      className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${loginMode === 'qrcode' ? 'bg-white text-slate-800 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      扫码登录
                    </button>
                  </div>

                  {/* 账号登录 */}
                  {loginMode === 'account' && (
                    <form onSubmit={handleLogin} className="space-y-4">
                      {/* 三合一输入框 */}
                      <div>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-slate-400" />
                          </div>
                          <input
                            type="text"
                            value={identifier}
                            onChange={(e) => { setIdentifier(e.target.value); setLoginError(''); }}
                            placeholder="手机号 / 邮箱 / 用户名"
                            className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                          />
                        </div>
                      </div>

                      {/* 密码输入 */}
                      <div>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Eye className="h-5 w-5 text-slate-400" />
                          </div>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                            placeholder="请输入密码"
                            className="w-full pl-11 pr-11 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center"
                          >
                            {showPassword ? <EyeOff className="h-5 w-5 text-slate-400" /> : <Eye className="h-5 w-5 text-slate-400" />}
                          </button>
                        </div>
                      </div>

                      {/* 错误提示 */}
                      {loginError && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm text-red-600">{loginError}</span>
                        </div>
                      )}

                      {/* 邮箱未验证提示 */}
                      {emailNotVerified && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-700">
                              <p>请先验证邮箱后再登录</p>
                              <button
                                type="button"
                                disabled={resending}
                                onClick={async () => {
                                  setResending(true);
                                  setResendMsg('');
                                  try {
                                    await post('/auth/resend-activation', { email: emailNotVerified });
                                    setResendMsg('激活邮件已重新发送，请查收');
                                  } catch (e) {
                                    setResendMsg(e instanceof Error ? e.message : '发送失败，请稍后重试');
                                  } finally {
                                    setResending(false);
                                  }
                                }}
                                className="mt-1.5 text-amber-600 hover:text-amber-700 underline underline-offset-2 text-xs"
                              >
                                {resending ? '发送中...' : '重新发送激活邮件'}
                              </button>
                              {resendMsg && <p className="mt-1 text-xs text-amber-600">{resendMsg}</p>}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 记住我和忘记密码 */}
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={() => setRememberMe(!rememberMe)}
                            className="w-4 h-4 text-teal-500 rounded border-slate-300 focus:ring-teal-500"
                          />
                          <span className="text-xs text-slate-600">记住我</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-xs text-teal-600 hover:text-teal-700 transition-colors"
                        >
                          忘记密码？
                        </button>
                      </div>

                      {/* 登录按钮 */}
                      <button 
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            登录中...
                          </>
                        ) : '登录'}
                      </button>

                      {/* 注册链接 */}
                      <p className="text-center text-xs text-slate-500">
                        还没有账号？
                        <button
                          type="button"
                          onClick={() => navigate('/register')}
                          className="text-teal-600 hover:text-teal-700 font-semibold ml-1"
                        >
                          立即注册
                        </button>
                      </p>
                    </form>
                  )}

                  {/* 扫码登录 */}
                  {loginMode === 'qrcode' && isDesktop && (
                    <ScanLoginQrCode />
                  )}

                  {/* 忘记密码已迁至独立路由 /forgot-password */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 功能介绍区 */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* 顶部标题区 */}
          <div className="text-center mb-16">
            {/* 徽章 */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 mb-5">
              <div className="w-1.5 h-1.5 bg-teal-500"></div>
              <span className="text-xs font-semibold tracking-wider uppercase text-teal-600">PLATFORM FEATURES</span>
            </div>
            {/* 主标题 */}
            <h4 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              <a href="#features" className="hover:text-teal-700 transition-colors">五大核心功能</a>，覆盖全业务链路
            </h4>
            {/* 副标题 */}
            <p className="text-base text-slate-500 leading-relaxed max-w-2xl mx-auto">
              预约订单 · 客户档案 · 收款对账，从下单、审核到导出台账，一个账号在线完成
            </p>
          </div>

          {/* 功能卡片网格 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 卡片1：智能预约日历管理 */}
            <div className="bg-teal-50 border border-white rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-5">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-3 transition-colors hover:text-teal-700">智能预约日历管理</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                可视化日历界面，实时查看每日预约情况，智能识别时间冲突并<strong className="text-teal-600 font-semibold">自动预警</strong>，让调度从未如此轻松。
              </p>
              <div className="mt-4 text-xs text-teal-500 opacity-0 transition-all duration-300 hover:opacity-100 hover:translate-x-1">
                了解更多 →
              </div>
            </div>

            {/* 卡片2：多维客户标签体系 */}
            <div className="bg-emerald-50 border border-white rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-5">
                <Tag className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-3 transition-colors hover:text-teal-700">多维客户标签体系</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                灵活自定义客户分类标签，构建精准客户画像，支持多维度筛选，<strong className="text-teal-600 font-semibold">精准触达</strong>目标客户群体。
              </p>
              <div className="mt-4 text-xs text-teal-500 opacity-0 transition-all duration-300 hover:opacity-100 hover:translate-x-1">
                了解更多 →
              </div>
            </div>

            {/* 卡片3：实时订单数据分析 */}
            <div className="bg-cyan-50 border border-white rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-5">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-3 transition-colors hover:text-teal-700">实时订单数据分析</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                多维数据看板实时呈现，订单趋势、客户分布、收入统计<strong className="text-teal-600 font-semibold">一目了然</strong>，业务洞察触手可及。
              </p>
              <div className="mt-4 text-xs text-teal-500 opacity-0 transition-all duration-300 hover:opacity-100 hover:translate-x-1">
                了解更多 →
              </div>
            </div>

            {/* 卡片4：订单对账核销 */}
            <div className="bg-teal-50 border border-white rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center mb-5">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-3 transition-colors hover:text-teal-700">订单对账核销</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                自动化对账流程，支持批量核销操作，财务数据精准对齐，<strong className="text-teal-600 font-semibold">彻底告别</strong>手工对账的低效困境。
              </p>
              <div className="mt-4 text-xs text-teal-500 opacity-0 transition-all duration-300 hover:opacity-100 hover:translate-x-1">
                了解更多 →
              </div>
            </div>

            {/* 卡片5：安全隔离数据权限 */}
            <div className="bg-emerald-50 border border-white rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-5">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-3 transition-colors hover:text-teal-700">安全隔离数据权限</h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                企业级多级权限控制，按数据角色隔离访问，操作日志全流程追溯，<strong className="text-teal-600 font-semibold">守护</strong>每一条业务数据安全。
              </p>
              <div className="mt-4 text-xs text-teal-500 opacity-0 transition-all duration-300 hover:opacity-100 hover:translate-x-1">
                了解更多 →
              </div>
            </div>

            {/* 卡片6：CTA卡片 */}
            <div className="bg-gradient-to-br from-teal-600 to-emerald-700 border border-white rounded-2xl p-7 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-5">
                  <Rocket className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-base font-bold text-white mb-3">立即开始使用</h4>
                <p className="text-teal-100 text-sm leading-relaxed">
                  免费注册，30秒完成配置，即刻体验全功能企业级订单预约管理服务。
                </p>
              </div>
              <button 
                onClick={() => navigate('/register')}
                className="mt-6 px-5 py-2.5 bg-white text-teal-700 text-sm font-semibold rounded-lg hover:bg-teal-50 transition-colors"
              >
                免费注册 →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 底部页脚 */}
      <footer className="bg-slate-800 text-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            {/* 左侧：Logo和品牌信息 */}
            <div className="flex items-center gap-3 mb-6 md:mb-0">
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto"
              />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>

            {/* 右侧：导航链接 */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/download" className="hover:text-white transition-colors">下载 App</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>

          {/* 底部备案信息 */}
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>

                {/* 注册弹窗已迁至独立路由 /register */}
    </div>
  );
}
